// Loads the Google Maps JS API on demand.
//
// This used to be a plain <script> tag in index.html, which meant every route
// the SPA serves paid for it — including the public, unauthenticated /f/:formId
// and /r/:formId/:customerId pages, where nothing uses Maps. Anonymous visitors
// on ad-blocked or filtered networks then hit Google's own `Could not load
// "places"` with no way to catch it, so it landed in Bugsnag as an unhandled
// error, and a customer-facing signup form opened a Google connection before
// any consent.
//
// Maps is only reachable from Settings → BranchList (AddressAutocomplete,
// MapPicker), so the load belongs there. The promise is memoised, so several
// components mounting together share one script tag and one request.
import { notifyError } from "../utils/bugsnag";

const SCRIPT_ID = "google-maps-js-api";

// Both libraries the branch UI needs: places for the address search,
// marker for the draggable AdvancedMarkerElement pin.
const LIBRARIES = ["places", "marker"];

// Backstop for a request that neither loads nor errors — a captive portal or a
// blocker that black-holes the response can leave the tag hanging indefinitely.
const LOAD_TIMEOUT_MS = 15000;

let loadPromise = null;

// Set once Google rejects our credentials. Auth is checked when a map or widget
// actually renders, so this can land long AFTER loadGoogleMaps() resolved — it
// is an event, not a rejection, which is why the script load itself succeeds and
// the merchant would otherwise just get Google's grey "something went wrong"
// overlay with nothing in Bugsnag and no message from us.
let authFailed = false;
const authListeners = new Set();

const alreadyLoaded = () =>
  LIBRARIES.every((library) => window.google?.maps?.[library]);

// A rejected key is an ops incident on our side, not something the merchant did:
// a wrong key, a referrer restriction that doesn't cover this origin, or billing
// switched off. The branch UI only tells them to contact support, so without an
// explicit report we find out by ticket — the load itself succeeded, so nothing
// reaches the global handler. Warning, not error: the dashboard degrades rather
// than breaking. No-ops outside staging/production (see utils/bugsnag).
const reportAuthFailure = () => {
  notifyError(new Error("Google Maps rejected the API key"), (event) => {
    event.context = "googleMapsLoader.gm_authFailure";
    event.severity = "warning";
    event.addMetadata("googleMaps", {
      // The usual culprit is the key's HTTP-referrer allowlist missing this
      // origin, so lead with it. Never the key itself.
      origin: window.location.origin,
      apiKeyConfigured: Boolean(import.meta.env.VITE_GOOGLE_MAPS_API_KEY),
      mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID",
      libraries: LIBRARIES.join(","),
    });
  });
};

// Google calls this global by name on InvalidKeyMapError, RefererNotAllowedMapError,
// billing being disabled, and friends. Installed before the script so the very
// first render is covered.
const installAuthFailureHook = () => {
  if (window.gm_authFailure?.isLoyaltifyHandler) return;

  const handler = () => {
    // Google can call this once per map/widget that tries to render, so only the
    // first rejection is worth reporting.
    if (!authFailed) reportAuthFailure();
    authFailed = true;
    // The API is poisoned for the life of the document once auth is rejected, so
    // drop the memo and the tag rather than letting alreadyLoaded() report success
    // off the half-initialised globals Google leaves behind.
    loadPromise = null;
    document.getElementById(SCRIPT_ID)?.remove();
    authListeners.forEach((listener) => listener());
  };
  handler.isLoyaltifyHandler = true;
  window.gm_authFailure = handler;
};

/**
 * Subscribe to Google rejecting our credentials. Fires immediately if it has
 * already happened, so a component mounting after the fact still degrades.
 * Returns an unsubscribe function.
 */
export const onGoogleMapsAuthFailure = (listener) => {
  authListeners.add(listener);
  if (authFailed) listener();
  return () => authListeners.delete(listener);
};

/** True once Google has rejected our API key for this document. */
export const isGoogleMapsAuthFailure = () => authFailed;

const injectScript = (apiKey) =>
  new Promise((resolve, reject) => {
    let existing = document.getElementById(SCRIPT_ID);

    // A tag can outlive the module that created it (dev HMR re-evaluates this
    // file and drops loadPromise, but the DOM is untouched). If that tag has
    // already fired its load event, listening for it again would wait forever,
    // so the listener below records the fact on the element itself. This also
    // covers a load we gave up on at LOAD_TIMEOUT_MS that arrived afterwards —
    // the listener still marks the tag even though its promise had rejected.
    if (existing?.dataset.loaded === "true") {
      resolve();
      return;
    }

    // A tag that already errored will never fire another event, so re-listening
    // would just wait out the timeout for nothing. Drop it and fetch afresh.
    if (existing?.dataset.failed === "true") {
      existing.remove();
      existing = null;
    }

    // Anything still here is a request in flight — in practice one an earlier
    // attempt timed out on. Wait on it rather than racing a duplicate: removing
    // a <script> never cancelled its fetch, so injecting a second tag is what
    // makes Google log "You have included the Google Maps JavaScript API
    // multiple times on this page" and leaves two bootstraps fighting.
    const script = existing ?? document.createElement("script");

    // Neither listener is guaranteed to fire — a stalled request produces no
    // event at all. Rejecting is always recoverable (callers degrade, and the
    // cached promise is dropped so a retry can succeed); a promise that never
    // settles is not, and would wedge the branch UI for the whole session.
    const timer = setTimeout(
      () => reject(new Error("Google Maps script timed out")),
      LOAD_TIMEOUT_MS,
    );

    script.addEventListener(
      "load",
      () => {
        clearTimeout(timer);
        script.dataset.loaded = "true";
        resolve();
      },
      { once: true },
    );
    // Fires when the request is blocked or fails outright (ad blocker, filtered
    // DNS, offline). Google's own library-level failures reject via
    // importLibrary below instead.
    script.addEventListener(
      "error",
      () => {
        clearTimeout(timer);
        script.dataset.failed = "true";
        reject(new Error("Google Maps script failed to load"));
      },
      { once: true },
    );

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&libraries=${LIBRARIES.join(",")}`;
      script.async = true;
      document.head.appendChild(script);
    }
  });

// Poll for the API the bootstrap pulls in behind the load event. Bounded by the
// same budget as the script fetch so a bootstrap that never completes rejects
// instead of spinning.
const waitForImportLibrary = async () => {
  const deadline = Date.now() + LOAD_TIMEOUT_MS;
  while (typeof window.google?.maps?.importLibrary !== "function") {
    if (Date.now() > deadline) {
      throw new Error("Google Maps bootstrapped without importLibrary");
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
};

/**
 * Resolve once google.maps and the libraries the branch UI needs are usable.
 * Rejects — catchably — when the script or a library can't be fetched; callers
 * are expected to degrade rather than crash.
 */
export const loadGoogleMaps = () => {
  // Unlike a blocked request, this is not something the merchant can retry their
  // way out of — the key or its referrer restrictions are wrong, and the API
  // stays poisoned until the document reloads.
  if (authFailed) {
    return Promise.reject(new Error("Google Maps rejected the API key"));
  }
  if (alreadyLoaded()) return Promise.resolve(window.google.maps);
  if (loadPromise) return loadPromise;

  installAuthFailureHook();

  // Read the key at call time (not module scope) so a missing key surfaces as a
  // rejection here rather than a mangled script URL.
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  loadPromise = (async () => {
    if (!apiKey) {
      throw new Error("VITE_GOOGLE_MAPS_API_KEY is not configured");
    }

    await injectScript(apiKey);

    // The load event does NOT mean the API is ready. With loading=async the
    // js?key= endpoint returns only a small bootstrap and the real API — including
    // importLibrary — arrives in a second request a few hundred ms later
    // (measured ~700ms on a cold load). Checking at the load event is therefore a
    // race that fails on a perfectly healthy load, so wait for it to show up.
    await waitForImportLibrary();

    // With loading=async the libraries arrive as further separate requests, which
    // is the step that actually fails on a blocked network. Awaiting them means a
    // broken load rejects here instead of leaving callers polling for a global
    // that never appears.
    await Promise.all(
      LIBRARIES.map((library) => window.google.maps.importLibrary(library)),
    );

    return window.google.maps;
  })().catch((error) => {
    // Drop the cached rejection so a later mount can retry — these failures are
    // usually transient (flaky mobile connection) or fixable by the user
    // (disabling a blocker), and a permanently poisoned promise would mean the
    // merchant has to reload the whole dashboard.
    loadPromise = null;
    // Only a tag that actually errored is worth clearing here, and injectScript
    // already replaces one of those on the next attempt. A tag we merely timed
    // out on stays: its request is still in flight, so a retry should wait on it
    // (or find it marked loaded) instead of injecting a second copy of the API.
    const script = document.getElementById(SCRIPT_ID);
    if (script?.dataset.failed === "true") script.remove();
    throw error;
  });

  return loadPromise;
};
